import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, input, signal, viewChild, inject } from '@angular/core';
import { IrisDmsModalsSandbox } from '@iris/common/modules/dms/sandbox/dms-modals.sandbox';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { IrisDmsFolderActionType, IrisDMSFolderI, IrisDmsRootFolderId } from '@iris/common/modules/dms/models/IrisDMSFolder';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { filter, finalize, take, tap } from 'rxjs/operators';
import { WindowRef } from '@iris/common/utils/window.ref';
import { IrisDmsPermissionsSandbox } from '@iris/common/modules/dms/sandbox/dms-permissions.sandbox';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { CommonModule } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IrisCommonDirectivesModule } from '@iris/common/modules/directives-common/common-directives.module';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisDmsActionsService } from '../../../services/dms-actions.service';
import { ClipboardService } from 'ngx-clipboard';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { IrisDmsEditFolderButtonComponent } from '../folder-actions/dms-edit-folder-button/dms-edit-folder-button.component';
import { IrisDmsShowFolderInfoButtonComponent } from '../folder-actions/dms-show-folder-info-button/dms-show-folder-info-button.component';
import { IrisDmsAddFolderButtonComponent } from '../folder-actions/dms-add-folder-button/dms-add-folder-button.component';
import { IrisDmsDownloadFolderButtonComponent } from '../folder-actions/dms-download-folder-button/dms-download-folder-button.component';
import { IrisDmsCopyFolderButtonComponent } from '../folder-actions/dms-copy-folder-button/dms-copy-folder-button.component';
import { IrisDmsImportZipFolderButtonComponent } from '../folder-actions/dms-import-zip-folder-button/dms-import-zip-folder-button.component';
import { IrisDmsRemoveFolderButtonComponent } from '../folder-actions/dms-remove-folder-button/dms-remove-folder-button.component';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';

@Component({
  selector: 'iris-dms-folder-actions-menu',
  templateUrl: './folder-actions-menu.component.html',
  styleUrls: ['./folder-actions-menu.component.scss'],
  imports: [
    CommonModule,
    MatDividerModule,
    MatMenuModule,
    TranslateModule,
    IrisCommonDirectivesModule,

    IrisDmsEditFolderButtonComponent,
    IrisDmsShowFolderInfoButtonComponent,
    IrisDmsAddFolderButtonComponent,
    IrisDmsDownloadFolderButtonComponent,
    IrisDmsCopyFolderButtonComponent,
    IrisDmsImportZipFolderButtonComponent,
    IrisDmsRemoveFolderButtonComponent,
    StrakitButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    IrisDmsActionsService,
  ],
})

export class IrisDmsFolderActionsMenuComponent implements AfterViewInit {
  private readonly dmsModalsSandbox = inject(IrisDmsModalsSandbox);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly windowRef = inject(WindowRef);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly dmsActionsService = inject(IrisDmsActionsService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  readonly folder = input<IrisDMSFolderI>();
  readonly folderId = computed(() => this.folder()?.id);
  private readonly parentFolderId = computed(() => this.folder()?.parentId);
  private readonly isFolderLocked = computed(() => !!this.folder()?.isLocked);
  private readonly isParentFolderLocked = computed(() => !!this.folder()?.isParentLocked);
  private readonly folderPath = computed(() => this.folder()?.path);

  private readonly currentProjectId = this.globalSandbox.currentProjectId;

  readonly isProjectFolder = computed(() => !!this.folder()?.isProjectFolder);
  readonly folderAndParentFolderAreNotLocked = computed(() => !this.isFolderLocked() && !this.isParentFolderLocked());
  readonly folderOrParentLocked = computed(() => !this.folderAndParentFolderAreNotLocked());
  readonly shouldShowEditButton = computed(() =>
    this.folderAndParentFolderAreNotLocked() && this.isActionNotDisabled(this.folderActions.Edit) && !this.isProjectFolder(),
  );
  get isAdmin(): boolean {
    return this.dmsPermissionsSandbox.isAdmin;
  }

  readonly cssClass = input<string>('');
  readonly disabledActions = input<IrisDmsFolderActionType[]>([]);
  readonly showWithTrigger = input<boolean>(true);
  readonly openFolderOnShowInfo = input<boolean>(false);
  readonly isTileView = input<boolean>(false);

  readonly trigger = viewChild(MatMenuTrigger);

  readonly folderActions = IrisDmsFolderActionType;

  readonly hasUpdatePermission = computed(() => this.dmsPermissionsSandbox.hasUpdatePermissionForFolderId(this.folderId())());
  readonly isCopyBtnDisabled = computed(() =>
    !this.hasUpdatePermission() || this.folderOrParentLocked() || this.parentFolderId() === IrisDmsRootFolderId,
  );
  readonly isMoveBtnDisabled = computed(() => this.isCopyBtnDisabled() || this.isProjectFolder());
  readonly isRemoveBtnDisabled = computed(() =>
    !this.hasUpdatePermission()
    || this.folderOrParentLocked()
    || this.parentFolderId() === IrisDmsRootFolderId && !this.dmsPermissionsSandbox.isAdmin
    || this.isProjectFolder(),
  );

  readonly outsideClickHandler = (): void => this.closeMenu();

  readonly rootFolderFixing = signal(false);
  readonly previewRegenerating = signal(false);
  readonly elasticSearchReindexing = signal(false);

  constructor() {
    effect(() => {
      const folderId = this.dmsFoldersSandbox.openedMenuFolderId();
      const fileId = this.dmsFilesSandbox.openedMenuFileId();

      const isThisMenuOpened = this.trigger()?.menuOpen && !!this.folder();
      const isOtherMenuOpening =
        !!fileId ||
        folderId && folderId !== this.folderId();

      if (isThisMenuOpened && isOtherMenuOpening) {
        this.closeMenu();
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.trigger()) {
      this.dmsPermissionsSandbox.getUserAvailableActionsForFolder(this.folderId());
      return;
    }
    const openMenu = this.trigger().openMenu;
    this.trigger().openMenu = (): void => {
      this.dmsPermissionsSandbox.getUserAvailableActions$(this.folderId()).pipe(
        take(1),
        filter((actions) => !!actions && !!actions.length),
        tap(() => openMenu.apply(this.trigger())),
      ).subscribe();
    };
  }

  public onMenuOpened(): void {
    this.dmsFoldersSandbox.setOpenedMenuFolderId(this.folderId());
    this.windowRef.nativeWindow.addEventListener('click', this.outsideClickHandler, false);
  }

  public closeMenu(): void {
    this.trigger()?.closeMenu();
  }

  public onMenuClosed(): void {
    this.dmsFoldersSandbox.setOpenedMenuFolderId(null);
    this.windowRef.nativeWindow.removeEventListener('click', this.outsideClickHandler, false);
  }

  public openMoveFolderModal(): void {
    this.dmsModalsSandbox.showMoveFolderModal(this.folderId());
  }

  public isActionNotDisabled(action: IrisDmsFolderActionType): boolean {
    return !this.disabledActions().includes(action);
  }

  shareLink(): void {
    this.dmsModalsSandbox.showFolderShareLinkModal(this.folder());
  }

  fixProjectRootFolder(): void {
    this.rootFolderFixing.set(true);
    this.dmsActionsService.repairRootFolder(this.currentProjectId()).pipe(
      take(1),
      finalize(() => this.rootFolderFixing.set(false)),
    ).subscribe();
  }

  regeneratePreviews(): void {
    this.previewRegenerating.set(true);
    this.dmsActionsService.regenerateFolderPreviews(this.folderPath()).pipe(
      take(1),
      finalize(() => this.previewRegenerating.set(false)),
    ).subscribe();
  }

  reindexElastic(): void {
    this.elasticSearchReindexing.set(true);
    this.dmsActionsService.reindexElastic(this.folderId()).pipe(
      take(1),
      finalize(() => this.elasticSearchReindexing.set(false)),
    ).subscribe();
  }

  copyFolderName(): void {
    this.clipboardService.copyFromContent(this.folder().name);
    this.toastService.success({ message: this.translateService.instant('text.dms.CopiedToClipboard') });
  }

  copyFolderPath(): void {
    this.clipboardService.copyFromContent(this.folder().path);
    this.toastService.success({ message: this.translateService.instant('text.dms.CopiedToClipboard') });
  }
}
